import { useContext, useEffect, useState, useCallback, useMemo } from 'react'
import {
  Stack,
  Text,
  PrimaryButton,
  Label,
  TextField,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
} from '@fluentui/react'
import { AppStateContext } from '../../state/AppProvider'
import { LLMSummaryResponse } from '../../api/models'
import { getLLMSummary, getEnvironment } from '../../api/api'
import MetricTrendsChart from '../../components/charts/MetricTrendsChart'
import PassFailChart from '../../components/charts/PassFailChart'
import TestCaseTable from '../../components/charts/TestCaseTable'
import { cleanMetricName } from '../../utils/metrics'
import LoadingSpinner from '../../components/common/LoadingSpinner'

const cardStyle = {
  root: {
    padding: '24px',
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
}

type StatCardProps = {
  title: string
  value: string | number
  color: string
}

const StatCard = ({ title, value, color }: StatCardProps) => (
  <Stack
    styles={{
      root: {
        flex: 1,
        minWidth: 140,
        padding: '20px 24px',
        backgroundColor: '#fff',
        borderRadius: 8,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        borderTop: `3px solid ${color}`,
      },
    }}
    tokens={{ childrenGap: 8 }}
  >
    <Text
      variant="small"
      styles={{ root: { color: '#605e5c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' } }}
    >
      {title}
    </Text>
    <Text variant="xxLarge" styles={{ root: { fontWeight: 700, color } }}>
      {value}
    </Text>
  </Stack>
)

const LLMResults = () => {
  const appStateContext = useContext(AppStateContext)
  const state = appStateContext?.state

  const getDefaultDates = () => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 14)
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    }
  }

  const dispatch = appStateContext?.dispatch

  // Monitoring test runs only exist for the production environment.
  const isProduction = getEnvironment() === 'production'

  const [startDate, setStartDate] = useState(state?.llmStartDate || getDefaultDates().startDate)
  const [endDate, setEndDate] = useState(state?.llmEndDate || getDefaultDates().endDate)
  const [data, setData] = useState<LLMSummaryResponse | null>(state?.llmData || null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null)

  const fetchData = useCallback(async (start: string, end: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await getLLMSummary(start, end)
      setData(result)
      // Default to the newest report (reports already sorted newest first)
      setSelectedReportId(result.reports.length > 0 ? result.reports[0].id : null)
      if (dispatch) {
        dispatch({ type: 'SET_LLM_DATA', payload: { data: result, startDate: start, endDate: end } })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load LLM results')
    } finally {
      setIsLoading(false)
    }
  }, [dispatch])

  useEffect(() => {
    if (!isProduction) return
    if (!state?.llmData) {
      fetchData(startDate, endDate)
    } else if (state.llmData.reports.length > 0 && !selectedReportId) {
      setSelectedReportId(state.llmData.reports[0].id)
    }
  }, [])

  // Sync when cached data updates
  useEffect(() => {
    if (state?.llmData && state.llmData.reports.length > 0 && !selectedReportId) {
      setSelectedReportId(state.llmData.reports[0].id)
    }
  }, [state?.llmData])

  const selectedReport = useMemo(() => {
    if (!data || !selectedReportId) return null
    return data.reports.find((r) => r.id === selectedReportId) || null
  }, [data, selectedReportId])

  const selected = selectedReport?.summary

  const reportOptions: IDropdownOption[] = useMemo(() => {
    if (!data) return []
    return data.reports.map((r) => ({
      key: r.id,
      text: r.time ? `${r.date} ${r.time}` : r.date,
    }))
  }, [data])

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
        Monitoring Test Results
      </Text>

      {!isProduction ? (
        <MessageBar messageBarType={MessageBarType.warning}>
          Monitoring Test Run Only on Production Environment
        </MessageBar>
      ) : (
      <>
      {/* Date Range Filter */}
      <Stack
        horizontal
        verticalAlign="end"
        tokens={{ childrenGap: 16 }}
        styles={cardStyle}
      >
        <Stack>
          <Label>Start Date</Label>
          <TextField
            type="date"
            value={startDate}
            onChange={(_, val) => val !== undefined && setStartDate(val)}
            styles={{ root: { width: 160 } }}
          />
        </Stack>
        <Stack>
          <Label>End Date</Label>
          <TextField
            type="date"
            value={endDate}
            onChange={(_, val) => val !== undefined && setEndDate(val)}
            styles={{ root: { width: 160 } }}
          />
        </Stack>
        <PrimaryButton
          text="Apply"
          onClick={() => fetchData(startDate, endDate)}
          disabled={isLoading}
        />
      </Stack>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      {isLoading && <LoadingSpinner label="Loading LLM results..." />}

      {!isLoading && data && (
        <Stack tokens={{ childrenGap: 20 }}>
          {/* Charts Row - always show full range */}
          <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { flexWrap: 'wrap' } }}>
            <MetricTrendsChart dates={data.trend_labels} metrics={data.metric_trends} />
          </Stack>

          <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { flexWrap: 'wrap' } }}>
            <PassFailChart data={data.pass_fail_trend} />
          </Stack>

          {/* Report Selector */}
          {data.reports.length > 0 && (
            <Stack
              horizontal
              verticalAlign="end"
              tokens={{ childrenGap: 12 }}
              styles={cardStyle}
            >
              <Stack>
                <Label>Report</Label>
                <Dropdown
                  selectedKey={selectedReportId || undefined}
                  options={reportOptions}
                  onChange={(_, option) => option && setSelectedReportId(option.key as string)}
                  styles={{ root: { minWidth: 240 } }}
                />
              </Stack>
              <Text variant="small" styles={{ root: { color: '#a19f9d', paddingBottom: 6 } }}>
                {data.reports.length} report{data.reports.length !== 1 ? 's' : ''} in range
              </Text>
            </Stack>
          )}

          {/* Summary Cards - driven by selected report */}
          {selected && (
            <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { flexWrap: 'wrap' } }}>
              <StatCard title="Total Tests" value={selected.total} color="#2B3A67" />
              <StatCard title="Passed" value={selected.passed} color="#5B8A72" />
              <StatCard title="Failed" value={selected.failed} color="#D4775B" />
              <StatCard
                title="Accuracy"
                value={`${(selected.accuracy * 100).toFixed(1)}%`}
                color="#496A99"
              />
            </Stack>
          )}

          {/* Metric Averages Cards - driven by selected report */}
          {selected && Object.keys(selected.metric_averages).length > 0 && (
            <Stack horizontal tokens={{ childrenGap: 12 }} styles={{ root: { flexWrap: 'wrap' } }}>
              {Object.entries(selected.metric_averages).map(([name, score]) => (
                <Stack
                  key={name}
                  styles={{
                    root: {
                      flex: 1,
                      minWidth: 160,
                      padding: '16px 20px',
                      backgroundColor: '#fff',
                      borderRadius: 8,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      borderLeft: `3px solid ${score >= 0.8 ? '#5B8A72' : score >= 0.6 ? '#B8C47E' : '#D4775B'}`,
                    },
                  }}
                  tokens={{ childrenGap: 4 }}
                >
                  <Text variant="small" styles={{ root: { color: '#605e5c', fontWeight: 600 } }}>
                    {cleanMetricName(name)}
                  </Text>
                  <Text
                    variant="xLarge"
                    styles={{
                      root: {
                        fontWeight: 700,
                        color: score >= 0.8 ? '#5B8A72' : score >= 0.6 ? '#B8C47E' : '#D4775B',
                      },
                    }}
                  >
                    {(score * 100).toFixed(1)}%
                  </Text>
                </Stack>
              ))}
            </Stack>
          )}

          {/* Test Case Table - driven by selected report */}
          {selectedReport && (
            <TestCaseTable
              results={selectedReport.results}
              date={selectedReport.time ? `${selectedReport.date} ${selectedReport.time}` : selectedReport.date}
            />
          )}
        </Stack>
      )}
      </>
      )}
    </Stack>
  )
}

export default LLMResults
